import { TextDivider } from '@discretize/react-discretize-components';
import Assessment from '@mui/icons-material/Assessment';
import ChildCare from '@mui/icons-material/ChildCare';
import Explore from '@mui/icons-material/Explore';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import School from '@mui/icons-material/School';
import Sync from '@mui/icons-material/Sync';
import {
  Box,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  SnackbarContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import EmbossedCard from '../../EmbossedCard';
import { Boon, Condition, Skill } from '@discretize/gw2-ui-new';
import Label from '../../Label';
import Rating from '../../Rating';
import { getCurrentPatch } from '../../../utils/patches';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const labelDifficulties = [
  'very easy',
  'easy',
  'average',
  'difficult',
  'challenging',
];
const labelRating = ['very low', 'low', 'average', 'high', 'very high'];

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(2),
  },
  gridItem: {
    [theme.breakpoints.down('sm')]: {
      '&:not(:first-of-type)': {
        paddingTop: '0 !important',
      },
      '&:first-of-type': {
        paddingBottom: '0 !important',
      },
    },
    [theme.breakpoints.down('lg')]: {
      '&:not(:first-of-type)': {
        boxShadow: `0 -1px 0 0 ${theme.palette.divider}`,
      },
    },
    [theme.breakpoints.up('md')]: {
      '&:not(:first-of-type)': {
        boxShadow: `-1px 0 0 0 ${theme.palette.divider}`,
      },
    },
  },
  listItem: {
    paddingTop: 2,
    paddingBottom: 2,
  },
  templateCode: {
    wordBreak: 'break-all',
    color: theme.palette.primary.main,
    padding: theme.spacing(1),
    alignSelf: 'center',
    marginRight: theme.spacing(0.5),
  },
  templateCodeSnackbar: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  icon: {
    fontSize: 18,
  },
  overviewDivider: {
    marginTop: theme.spacing(2),
  },
}));

interface FrontmatterProps {
  title: string;
  profession: string;
  code?: string;
  boons?: Array<{
    name: string;
    variant?: string;
    uptime: string;
  }>;
  classification?: number[];
  skills?: number[];
  conditions?: Array<{
    name: string;
    variant?: string;
    uptime: string;
  }>;
  role?: string;
  date?: string;
}

export default function Frontmatter({
  title,
  profession,
  code,
  boons,
  skills,
  conditions,
  classification,
  role,
  date,
}: FrontmatterProps) {
  const { classes } = useStyles();

  const [state, setState] = React.useState({ copiedCode: false });

  return (
    <>
      <div>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          open={state.copiedCode}
          autoHideDuration={3000}
          onClose={() => setState({ copiedCode: false })}
        >
          <SnackbarContent
            message={`Copied ${title} template code to clipboard`}
            className={classes.templateCodeSnackbar}
          />
        </Snackbar>
      </div>
      <TextDivider className={classes.overviewDivider}>
        <>Overview</>
      </TextDivider>
      <EmbossedCard classes={{ content: classes.card }} profession={profession}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} className={classes.gridItem}>
            <Table>
              <TableBody>
                {date && (
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Up-to-date
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getCurrentPatch().date > new Date(date.trim()) ? (
                          <>
                            <CancelIcon sx={{ color: 'yellow' }} />
                            <Typography variant="body2" ml={1}>
                              we are working on it...
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon />
                            <Typography variant="body2" ml={1}>
                              updated for the{' '}
                              {new Date(
                                getCurrentPatch().date,
                              ).toLocaleDateString()}{' '}
                              patch
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {code && (
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Template Code
                    </TableCell>
                    <TableCell>
                      <Box display="flex">
                        <code className={classes.templateCode}>{code}</code>

                        <Tooltip title="Copy to clipboard">
                          <IconButton
                            aria-label="copy"
                            size="large"
                            onClick={() => {
                              setState({ copiedCode: true });
                              if (
                                typeof navigator !== 'undefined' &&
                                navigator.clipboard
                              ) {
                                navigator.clipboard.writeText(code);
                              }
                            }}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {boons && boons.length > 0 && (
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Boons
                    </TableCell>
                    <TableCell>
                      <List dense>
                        {boons.map(({ name, variant, uptime }) => (
                          <ListItem
                            key={`${name}-${variant}`}
                            disableGutters
                            className={classes.listItem}
                          >
                            <ListItemText
                              primary={
                                <>
                                  <Boon name={name as any} />: {uptime}{' '}
                                  {variant && <>- {variant}</>}
                                </>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </TableCell>
                  </TableRow>
                )}
                {skills && skills.length > 0 && (
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Key Skills
                    </TableCell>
                    <TableCell>
                      <List dense>
                        {skills.map((id) => (
                          <ListItem
                            key={`${id}`}
                            disableGutters
                            className={classes.listItem}
                          >
                            <ListItemText primary={<Skill id={id} />} />
                          </ListItem>
                        ))}
                      </List>
                    </TableCell>
                  </TableRow>
                )}
                {conditions && conditions.length > 0 && (
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Conditions
                    </TableCell>
                    <TableCell>
                      <List dense>
                        {conditions.map(({ name, variant, uptime }) => (
                          <ListItem
                            key={`${name}-${variant}`}
                            disableGutters
                            className={classes.listItem}
                          >
                            <ListItemText
                              primary={
                                <>
                                  <Condition name={name as any} />
                                  {uptime && <>: {uptime}</>}
                                  {variant && <>- {variant}</>}
                                </>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </TableCell>
                  </TableRow>
                )}
                {[{ label: 'Role', variable: role }].map(
                  ({ label, variable }) =>
                    variable && (
                      <TableRow key={label}>
                        <TableCell component="th" scope="row">
                          {label}
                        </TableCell>
                        <TableCell>
                          <List dense>
                            <ListItem
                              key={12}
                              disableGutters
                              className={classes.listItem}
                            >
                              <ListItemText primary={variable} />
                            </ListItem>
                          </List>
                        </TableCell>
                      </TableRow>
                    ),
                )}
              </TableBody>
            </Table>
          </Grid>

          <Grid item xs={12} sm={6} className={classes.gridItem}>
            {classification && (
              <Table>
                <TableBody>
                  <>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Sync className={classes.icon} /> Synergy
                      </TableCell>
                      <TableCell>
                        <Rating value={classification[0]} />
                        <Label> {labelRating[classification[0] - 1]}</Label>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Assessment className={classes.icon} /> DPS
                      </TableCell>
                      <TableCell>
                        <Rating value={classification[1]} />
                        <Label> {labelRating[classification[1] - 1]}</Label>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Explore className={classes.icon} /> Independence
                      </TableCell>
                      <TableCell>
                        <Rating value={classification[2]} />
                        <Label> {labelRating[classification[2] - 1]}</Label>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <ChildCare className={classes.icon} /> Acquiring basics
                      </TableCell>
                      <TableCell>
                        <Rating value={6 - classification[3]} />
                        <Label>
                          {labelDifficulties[classification[3] - 1]}
                        </Label>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <School className={classes.icon} /> Achieving mastery
                      </TableCell>
                      <TableCell>
                        <Rating value={6 - classification[4]} />
                        <Label>
                          {labelDifficulties[classification[4] - 1]}
                        </Label>
                      </TableCell>
                    </TableRow>
                  </>
                </TableBody>
              </Table>
            )}
          </Grid>
        </Grid>
      </EmbossedCard>
    </>
  );
}
